import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { TenantContext } from '@interxfi/api-client';
import { useIxmUserauthV1TenantQueryServiceTenantGet } from '@interxfi/api-client/generated/hooks/ixm-userauth-v1-tenant-query-service-tenant-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxmUserauthV1TenantState } from '@interxfi/types/generated/api';
import { UserauthTenantEntityMenu } from '../../../../../../features/userauth/tenant/userauth-tenant-entity-menu';
import { TenantEventSummary } from '../../../../../../features/userauth/tenant/userauth-tenant-event';
import { getUserauthTenantStateListItems } from '../../../../../../features/userauth/tenant/userauth-tenant-list-items';
import { getUserauthTenantDetailPath } from '../../../../../../features/userauth/tenant/userauth-tenant-routing';
import { TenantStatusPill } from '../../../../../../features/userauth/tenant/userauth-tenant-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../util/error-handler';
import type { RouteGuardOptions } from '../../../../../../util/routing/guard';
import { getPageHead } from '../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_platform/_context-required/tenant/_tenant/$tenantId')({
  staticData: {
    guard: { access: { allowedTenantTypes: [TenantContext.Platform] } },
  } as const satisfies RouteGuardOptions,
  shouldReload: true,
  loader: ({ params }) => ({
    breadcrumb: buildBreadcrumb({
      id: `tenant/${params.tenantId}`,
      label: params.tenantId,
      to: getUserauthTenantDetailPath(params.tenantId),
    }),
  }),
  head: () => getPageHead('Tenant'),
  component: RouteComponent,
});

function getListItems(data: IxmUserauthV1TenantState | undefined) {
  const items = getUserauthTenantStateListItems(data);

  return {
    left: [items.data.spec.name, items.tenantType] as const satisfies ReadonlyListItem[],
    right: [items.metadata.createdAt, items.metadata.updatedAt, items.tenantId, items.realmId] as const satisfies ReadonlyListItem[],
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load tenant', 'view this tenant');

function RouteComponent() {
  const { tenantId } = Route.useParams();
  const request = { tenantId } as const;
  const { data, error, isPending } = useIxmUserauthV1TenantQueryServiceTenantGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.tenant), [data?.tenant]);

  return (
    <PageLayout gap={7} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack>
            <TenantStatusPill isPending={showPending} size="large" status={data?.tenant?.status} />
          </HorizontalStack>

          <UserauthTenantEntityMenu tenantId={tenantId} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <TenantEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
